import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHref } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useJotRxSetupState } from '@features/eprescriptions';
import { useRecordMedicationHistoryConsentMutation } from '@features/eprescriptions/store/api';
import {
  useMedicationsQuery,
  useRemoveMedicationMutation,
  useUpdateMedicationMutation,
} from '@features/patients/store/api';
import { useSubjectLanguageVariants } from '@features/user/store/hooks';
import { Box, Skeleton, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppDispatch } from '@store/hooks/core';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { ClinicalCardFrame } from '../components/ClinicalCardFrame';
import { MedicationsCard } from '../components/MedicationsCard';
import { medicationsWorkspaceApi, useUnifiedMedicationsQuery } from '../store/api';
import { mapUnifiedToVM } from '../vm/mapUnifiedToVM';
import type { UnifiedMedication } from '../vm/unifiedApiTypes';

interface V1MedicationsCardProps {
  patientId: string;
  /**
   * Fire the cardOpened impression on mount (default). Hosts that embed this
   * card inside another tracked surface (the context-bar edit modal, which
   * fires editModalOpened) pass false so one open isn't counted twice.
   */
  trackImpression?: boolean;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: the dialog's close handler, rendered in the header. */
  onClose?: () => void;
  /** Drops the card's own title for hosts that already render one (the V3 section shell). */
  hideTitle?: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  surface?: 'patient_page_v1' | 'patient_page_v3' | 'context_bar' | 'encounter';
  prescribingAction?: React.ReactNode;
  tableMaxHeight?: number | string;
}

/** Undo handle returned by an optimistic RTK Query cache patch. */
interface CachePatch {
  undo: () => void;
}

/** Poll cadence while a post-consent SureScripts pull is in flight. */
const PULLING_POLL_MS = 3000;

/**
 * Data glue for the unified Medications card on Patient Page V3 and the
 * clinical edit modal. Reads Component D's unified history, writes through the
 * existing per-resource medication CRUD (add/edit via the shared
 * AddMedicationModal), and drives the consent-gated SureScripts section.
 *
 * Write contract (e-plan §3.6): discontinue sends `{ is_active: false }` — the
 * backend silently DISCARDS a bare `status` string; Remove is the soft DELETE
 * (the row survives server-side as inactive_reason='removed' and the mapper
 * filters it out of the view).
 */
export const V1MedicationsCard: React.FC<V1MedicationsCardProps> = ({
  patientId,
  trackImpression = true,
  variant = 'card',
  onClose,
  hideTitle,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  surface = 'patient_page_v1',
  prescribingAction,
  tableMaxHeight,
}) => {
  const [pollMs, setPollMs] = useState(0);
  const { data, isLoading, isError, refetch } = useUnifiedMedicationsQuery(
    { patientId },
    { pollingInterval: pollMs },
  );
  // The v1 spine list backs the edit modal's field set (form/route/timing…),
  // which the unified projection doesn't carry. Same cache family; cheap.
  // The card waits for it: AddMedicationModal seeds react-hook-form from
  // defaultValues once at mount, so opening Edit before the spine resolves
  // would show a blank form whose save blanks real fields.
  const { data: spineData, isLoading: isSpineLoading } = useMedicationsQuery({
    patientId,
  });

  const [updateMedication] = useUpdateMedicationMutation();
  const [removeMedication] = useRemoveMedicationMutation();
  const [recordConsent] = useRecordMedicationHistoryConsentMutation();
  const dispatch = useAppDispatch();
  const { open } = useModal();

  // Optimistic cache patch: the row reflects the action instantly instead of
  // waiting for mutation + tag-invalidated refetch (~seconds of dead UI).
  // The refetch still reconciles truth on success; the patch reverts on error.
  const patchUnifiedMedication = useCallback(
    (medicationId: string, mutate: (medication: UnifiedMedication) => void) =>
      dispatch(
        medicationsWorkspaceApi.util.updateQueryData(
          'getV2ProviderPatientsByPatientIdMedicationsUnifiedHistory',
          { patientId },
          draft => {
            const target = (draft.medications ?? []).find(
              medication => medication.medication_id === medicationId,
            );
            if (target) mutate(target);
          },
        ),
      ) as unknown as CachePatch,
    [dispatch, patientId],
  );
  const { enqueueSnackbar } = useSnackbar();
  const { unifiedMedications: tracking, medicationHistory: consentTracking } = useEventTracking();
  // Default OFF → suggestions hidden; an org must opt in to see them.
  const showSuggestions = useFeatureGate(FeatureGates.SHOW_CHART_SUGGESTIONS);
  const { singular: subject } = useSubjectLanguageVariants();
  // SureScripts history is only reachable through DoseSpot — without an
  // e-prescribing setup the section (and its consent CTA) is dead weight.
  // The skeleton gate below waits for this hook too: isFullySetup reads
  // false while its queries load, which would flash the section hidden.
  const { isFullySetup: providerHasDoseSpot, isLoading: isSetupLoading } = useJotRxSetupState();
  // The prescribe launcher validates the patient, mints the DoseSpot SSO URL,
  // and redirects — opened in a new tab so the chart stays put.
  const prescribeHref = useHref(`/jotrx/prescribe-launch/${patientId}?source=unified_meds_card`);

  const cardData = useMemo(() => (data ? mapUnifiedToVM(data) : undefined), [data]);

  // Poll while the post-consent pull is in flight so the section flips to
  // granted without a reload (mirrors the shipped V2 section's behavior).
  // Read via the mapper so the missing-consent fallback lives in one place.
  const consentState = cardData?.consentState;
  useEffect(() => {
    setPollMs(consentState === 'pulling' ? PULLING_POLL_MS : 0);
  }, [consentState]);

  // One impression per mount, once the unified data is in.
  const openedTracked = useRef(false);
  useEffect(() => {
    if (!trackImpression || openedTracked.current || !cardData || !consentState) return;
    openedTracked.current = true;
    tracking.cardOpened({
      patient_id: patientId,
      medication_count: cardData.workspace.episodes.length,
      history_fill_count: cardData.historyFills.length,
      consent_state: consentState,
      surface,
    });
  }, [cardData, consentState, patientId, surface, tracking, trackImpression]);

  const spineById = useMemo(
    () => new Map((spineData?.medications ?? []).map(med => [med.medication_id, med])),
    [spineData],
  );

  // Pending AI chart-proposals ride the unified response; their action_id is
  // accepted by the same PATCH/DELETE medication routes (accept-with-edits /
  // dismiss), so the row callbacks below just branch on membership here.
  const suggestedById = useMemo(
    () =>
      new Map(
        (data?.suggested_medications ?? []).map(suggested => [suggested.action_id, suggested]),
      ),
    [data],
  );

  const handleAdd = useCallback(() => {
    tracking.addClicked({ patient_id: patientId, surface });
    open(MODAL_TYPES.ADD_MEDICATION, { patientId });
  }, [open, patientId, surface, tracking]);

  const handleEdit = useCallback(
    (medicationId: string) => {
      tracking.editClicked({ patient_id: patientId, medication_id: medicationId, surface });
      const suggested = suggestedById.get(medicationId);
      if (suggested) {
        // Review & accept: the modal opens prefilled from the proposal;
        // saving PATCHes the action_id, which accepts-with-edits (CUS-600).
        open(MODAL_TYPES.ADD_MEDICATION, {
          patientId,
          medicationId,
          defaultValues: {
            name: suggested.name,
            generic_name: suggested.generic_name ?? '',
            strength: suggested.strength ?? '',
            form: suggested.form ?? '',
            route: suggested.route ?? '',
            dosage: suggested.dosage ?? '',
            frequency: suggested.frequency ?? '',
            timing: suggested.timing ?? '',
            start_date: suggested.start_date ?? '',
            end_date: suggested.end_date ?? '',
            indication: suggested.indication ?? '',
            is_active: true,
          },
        });
        return;
      }
      const med = spineById.get(medicationId);
      if (!med) {
        enqueueSnackbar('Medication details are still loading — try again in a moment.', {
          variant: 'info',
        });
        return;
      }
      open(MODAL_TYPES.ADD_MEDICATION, {
        patientId,
        medicationId,
        defaultValues: {
          name: med.name,
          generic_name: med.generic_name ?? '',
          strength: med.strength ?? '',
          form: med.form ?? '',
          route: med.route ?? '',
          dosage: med.dosage ?? '',
          frequency: med.frequency ?? '',
          timing: med.timing ?? '',
          start_date: med.start_date ?? '',
          end_date: med.end_date ?? '',
          indication: med.indication ?? '',
          is_active: med.is_active ?? true,
        },
      });
    },
    [enqueueSnackbar, open, patientId, spineById, suggestedById, surface, tracking],
  );

  const handleDiscontinue = useCallback(
    (medicationId: string) => {
      tracking.discontinueClicked({ patient_id: patientId, medication_id: medicationId, surface });
      const patch = patchUnifiedMedication(medicationId, medication => {
        medication.status = 'discontinued';
      });
      void updateMedication({
        patientId,
        medicationId,
        updateMedicationRequest: { is_active: false },
      })
        .unwrap()
        .catch(() => {
          patch.undo();
          enqueueSnackbar('Could not discontinue the medication. Please try again.', {
            variant: 'error',
          });
        });
    },
    [updateMedication, patchUnifiedMedication, patientId, enqueueSnackbar, surface, tracking],
  );

  const handleRemove = useCallback(
    (medicationId: string) => {
      tracking.removeClicked({ patient_id: patientId, medication_id: medicationId, surface });
      if (suggestedById.has(medicationId)) {
        // Dismissing a proposal: DELETE on the action_id rejects it; the
        // optimistic patch drops it from the suggested list immediately.
        const patch = dispatch(
          medicationsWorkspaceApi.util.updateQueryData(
            'getV2ProviderPatientsByPatientIdMedicationsUnifiedHistory',
            { patientId },
            draft => {
              draft.suggested_medications = (draft.suggested_medications ?? []).filter(
                suggested => suggested.action_id !== medicationId,
              );
            },
          ),
        ) as unknown as CachePatch;
        void removeMedication({ patientId, medicationId })
          .unwrap()
          .catch(() => {
            patch.undo();
            enqueueSnackbar('Could not dismiss the suggestion. Please try again.', {
              variant: 'error',
            });
          });
        return;
      }
      // 'removed' is the soft-DELETE bucket the mapper filters out, so the
      // row disappears immediately.
      const patch = patchUnifiedMedication(medicationId, medication => {
        medication.status = 'inactive';
        medication.inactive_reason = 'removed';
      });
      void removeMedication({ patientId, medicationId })
        .unwrap()
        .catch(() => {
          patch.undo();
          enqueueSnackbar('Could not remove the medication. Please try again.', {
            variant: 'error',
          });
        });
    },
    [
      dispatch,
      removeMedication,
      patchUnifiedMedication,
      patientId,
      enqueueSnackbar,
      suggestedById,
      surface,
      tracking,
    ],
  );

  const handleRecordConsent = useCallback(() => {
    consentTracking.consentClicked({ patient_id: patientId });
    void recordConsent({ patientId })
      .unwrap()
      .then(() => {
        consentTracking.consentSubmitted({ patient_id: patientId });
        // The consent mutation lives in the eprescriptions slice and doesn't
        // know this feature's UnifiedMedications tag, so kick the unified
        // query directly: the refetch observes 'pulling', which turns the
        // poll on until the SureScripts pull lands and the section flips to
        // granted — no page reload.
        void refetch();
      })
      .catch(() => {
        enqueueSnackbar(
          `Could not record consent. Confirm the ${subject} is connected to e-prescribing and try again.`,
          { variant: 'error' },
        );
      });
  }, [recordConsent, refetch, patientId, enqueueSnackbar, consentTracking, subject]);

  const handleFilterChanged = useCallback(
    (filter: 'all' | 'active' | 'inactive') => {
      tracking.filterChanged({ patient_id: patientId, filter, surface });
    },
    [tracking, patientId, surface],
  );

  const handleManageInDoseSpot = useCallback(
    (medicationId: string) => {
      tracking.manageRxClicked({ patient_id: patientId, medication_id: medicationId, surface });
      window.open(prescribeHref, '_blank', 'noopener,noreferrer');
    },
    [patientId, prescribeHref, surface, tracking],
  );

  const handleHistoryToggled = useCallback(
    (medicationId: string, expanded: boolean) => {
      tracking.historyToggled({
        patient_id: patientId,
        medication_id: medicationId,
        expanded,
        surface,
      });
    },
    [tracking, patientId, surface],
  );

  const handleHistorySectionToggled = useCallback(
    (expanded: boolean) => {
      consentTracking.sectionToggled({
        patient_id: patientId,
        expanded,
        fill_count: cardData?.historyFills.length ?? 0,
      });
    },
    [consentTracking, patientId, cardData],
  );

  const suggestedCount = useMemo(
    () => cardData?.workspace.episodes.filter(episode => episode.isSuggested).length ?? 0,
    [cardData],
  );

  const handleSuggestionsSectionToggled = useCallback(
    (expanded: boolean) => {
      tracking.suggestionsSectionToggled({
        patient_id: patientId,
        expanded,
        suggested_count: suggestedCount,
        surface,
      });
    },
    [tracking, patientId, suggestedCount, surface],
  );

  if (isLoading || isSpineLoading || isSetupLoading || !cardData) {
    return (
      <ClinicalCardFrame
        title="Medications"
        hideTitle={hideTitle}
        variant={variant}
        onClose={onClose}
        // Prescribing reads JotRx setup state, not the unified history — a
        // failed history read must not take the CTA down with it. Error only:
        // the success path renders it once data lands, and Add stays out
        // because the card couldn't reflect the new row while the read is down.
        actions={isError ? prescribingAction : undefined}
      >
        {isError ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 4, textAlign: 'center' }}
          >
            Medications could not be loaded. Refresh to try again.
          </Typography>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            {[0, 1, 2].map(row => (
              <Skeleton key={row} variant="rounded" height={44} sx={{ my: 1, borderRadius: 1 }} />
            ))}
          </Box>
        )}
      </ClinicalCardFrame>
    );
  }

  return (
    <MedicationsCard
      workspace={cardData.workspace}
      consentState={cardData.consentState}
      historyFills={cardData.historyFills}
      attributedFillCount={cardData.attributedFillCount}
      onAdd={canAdd ? handleAdd : undefined}
      onEdit={canEdit ? handleEdit : undefined}
      onManageInDoseSpot={handleManageInDoseSpot}
      onDiscontinue={canDelete ? handleDiscontinue : undefined}
      onRemove={canDelete ? handleRemove : undefined}
      onRecordConsent={handleRecordConsent}
      onFilterChanged={handleFilterChanged}
      onHistoryToggled={handleHistoryToggled}
      onHistorySectionToggled={handleHistorySectionToggled}
      hideSuggestions={!showSuggestions}
      onSuggestionsSectionToggled={handleSuggestionsSectionToggled}
      showHistory={providerHasDoseSpot}
      variant={variant}
      onClose={onClose}
      hideTitle={hideTitle}
      subject={subject}
      prescribingAction={prescribingAction}
      tableMaxHeight={tableMaxHeight}
    />
  );
};
